import React, { useCallback, useMemo, useState } from "react";
import { useController } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import z from "zod";

import { Button, Divider, Form, Input, Switch } from "@skydio/rivet-ui";

import { CLOUD_API_URL } from "app/env";
import { useCreateOidcIdentityProvider } from "hooks/mutation/createOidcIdentityProvider";
import { displayMutationResults } from "utils/mutations";
import { getUuidFromGlobalId } from "utils/relay";

import { SsoHelpLink } from "./SsoHelpLink";
import { ResultRow } from "./SsoResultRow";

export interface OidcUrls {
  callbackUrl: string;
  initiateLoginUrl: string;
}

type usePkce = {
  usePkce: boolean;
};
interface PkceToggleProps {
  loading: boolean;
}

// Rivet switch wired into form
const PkceToggle = (props: PkceToggleProps) => {
  const { field } = useController<usePkce, "usePkce">({ name: "usePkce" });

  return (
    <div className="group relative flex flex-col justify-end gap-y-1">
      <label
        className="pointer-events-none font-semibold text-gray-900 dark:text-gray-100"
        htmlFor="usePkce"
      >
        Use PKCE
      </label>
      <Switch
        id="usePkce"
        size="large"
        loading={props.loading}
        checked={field.value}
        onCheckedChange={field.onChange}
        name="usePkce"
      />
      <div className="text-xs text-gray-400" data-slot="description">
        Use PKCE for the OIDC provider
      </div>
    </div>
  );
};

interface NewOidcProviderPaneProps {
  organizationNodeId: string;
  connection?: string;
  onNext?: () => void;
  onCancel?: () => void;
  // newUuid?: string;
}

const newOidcSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: "Name must not be empty" })
      .max(255, { message: "Name must be less than 255 characters" }),
    clientId: z.string().min(1, { message: "Client ID must not be empty" }),
    clientSecret: z.string().nullable(),
    usePkce: z.boolean().default(false),
    wellKnownUrl: z.string().url(),
    // providerJson: z.string(), // TODO add file upload when we have text area input for form
  })
  .refine(
    data => {
      return !data.usePkce ? data.clientSecret != "" : true;
    },
    { message: "Client secret must not be empty if PKCE is not used", path: ["clientSecret"] }
  );

const NewOidcProviderPane = ({
  organizationNodeId,
  connection,
  onNext,
  onCancel,
  // newUuid,
}: NewOidcProviderPaneProps) => {
  const [newUuid, setNewUuid] = useState<string>(uuidv4());
  const [createOidcProvider, loading] = useCreateOidcIdentityProvider(
    displayMutationResults(response => response.createOidcIdentityProvider, {
      onSuccess: () => {
        onNext?.();
        setNewUuid(uuidv4());
      },
      successMessage: () => "OIDC provider created successfully",
    })
  );

  const callbackUrl = useMemo(() => {
    return `${CLOUD_API_URL}/oidc/${getUuidFromGlobalId(organizationNodeId)}/${newUuid}/callback`;
  }, [newUuid, organizationNodeId]);

  const initiateLoginUrl = useMemo(() => {
    return `${CLOUD_API_URL}/oidc/login/${getUuidFromGlobalId(organizationNodeId)}/${newUuid}`;
  }, [newUuid, organizationNodeId]);

  const handleSubmit = useCallback(
    (values: z.infer<typeof newOidcSchema>) => {
      createOidcProvider(
        organizationNodeId,
        values.name,
        values.clientId,
        values.clientSecret,
        values.usePkce,
        values.wellKnownUrl,
        [connection ?? ""],
        newUuid ?? null
      );
    },
    [createOidcProvider, organizationNodeId, connection, newUuid]
  );

  const oidcWellKnownDescription = useMemo(
    () => (
      <div>
        URL for OIDC provider metadata e.g.:
        <br />
        https://company.okta.com/.well-known/openid-configuration <br />
        https://login.microsoftonline.com/company/v2.0/.well-known/openid-configuration
      </div>
    ),
    []
  );

  return (
    <>
      <div className="grid size-auto grid-cols-12 items-stretch justify-items-stretch gap-2">
        <ResultRow label="Callback URL" value={callbackUrl} />
        <ResultRow label="Initiate Login URL" value={initiateLoginUrl} />
      </div>
      <Divider />
      <Form
        schema={newOidcSchema}
        onSubmit={handleSubmit}
        className="flex flex-col items-start gap-2"
      >
        <>
          <div className="flex w-full flex-col gap-4">
            <Input
              name="name"
              label={
                <>
                  Name <span className="text-red-500">*</span>
                </>
              }
              placeholder="Name"
              isClearable
              isDisabled={loading}
              description="Name of Identity Provider. Required"
            />
            <Input
              name="clientId"
              label={
                <>
                  Client ID <span className="text-red-500">*</span>
                </>
              }
              placeholder="Client ID"
              isClearable
              isDisabled={loading}
              description="Client ID for integration. Required"
            />
            <Input
              name="clientSecret"
              type="password"
              label={
                <>
                  Client Secret <span className="text-red-500">*</span>
                </>
              }
              placeholder="Client Secret"
              isClearable
              isDisabled={loading}
              description="Client Secret for integration"
            />
            <PkceToggle loading={loading} />
            <Input
              name="wellKnownUrl"
              label={
                <>
                  OpenID Well Known URL <span className="text-red-500">*</span>
                </>
              }
              placeholder="https://provider.example.com/.well-known/openid-configuration"
              isClearable
              isDisabled={loading}
              description={oidcWellKnownDescription}
            />
          </div>
          <span className="flex w-full justify-between">
            <SsoHelpLink />
            <div className="flex gap-4 self-end">
              <Button onClick={onCancel}>Cancel</Button>
              <Button isLoading={loading} type="submit" id="createProvider" variant="primary">
                Create
              </Button>
            </div>
          </span>
        </>
      </Form>
    </>
  );
};

export default NewOidcProviderPane;
